import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    QueryList,
    ViewChild,
} from '@angular/core';
import { AbcpDocumentNotesSlotDirective, AbcpDocumentViewerSlotDirective } from './document-detail-slots.directive';
import {
    AccountOption,
    DocumentUpstreamState,
    DocumentValidity,
    MarkNotRelevantEvent,
} from '../../types/closing-documents.types';
import {
    DocumentAssignmentChangeEvent,
    DocumentDetail,
    DocumentDetailBusy,
    DocumentInterpretationView,
    DocumentSidebarState,
    DocumentValidityChangeEvent,
    DocumentValueLabel,
} from './document-detail.types';
import {
    INTERPRETATION_LIMIT_REACHED_LABEL_KEY,
    INTERPRETATION_REASON_KEYS,
    PROPOSAL_REFUSAL_KEYS,
    PROPOSAL_REFUSAL_TITLE_KEY,
    READING_BLOCK_KEYS,
    READING_STATE_LABEL_KEYS,
    feedbackKeyOf,
    isoDateLabel,
    quoteTextOf,
} from '../../presenters/document-feedback-keys';

/** `value` → `value`, `noValue` → `no-value` — the DTO's camelCase, the wire's kebab-case attribute. */
const INTERPRETATION_DATA_STATE: Record<DocumentInterpretationView['state'], string> = {
    value: 'value',
    interpreting: 'interpreting',
    noValue: 'no-value',
    none: 'none',
    disabled: 'disabled',
};

/**
 * The named empty-state label per state — never a raw code (P12/N3/N4).
 *
 * ⚠️ REV-22010 (product decision, taken by the user): "Weg c" — the empty state NEVER shows a reason —
 * is LIFTED. The label stays the headline; UNDER it the panel may now name WHY, as a reason from a
 * CLOSED list the backend maps its refusal codes onto (`INTERPRETATION_REASON_KEYS`,
 * `presenters/document-feedback-keys.ts`) — never a raw code, never an amount, never free text. Weg c
 * was right while aumico-be had no carrier for a reason (and a reason nobody knows must not be
 * claimed); REV-22010's `REFUSAL` stage is that carrier. No reason on the wire ⇒ still no reason here.
 */
const INTERPRETATION_STATE_LABEL_KEY: Record<Exclude<DocumentInterpretationView['state'], 'value'>, string> = {
    interpreting: 'abcp_document_detail_interpretation_interpreting',
    noValue: 'abcp_document_detail_interpretation_no_value',
    none: 'abcp_document_detail_interpretation_none',
    disabled: 'abcp_document_detail_interpretation_disabled',
};

/** What each read value is called on its line. */
const VALUE_LABEL_KEYS: Record<DocumentValueLabel, string> = {
    BALANCE: 'abcp_document_detail_value_balance',
    MARKET_VALUE: 'abcp_document_detail_value_market_value',
    COST: 'abcp_document_detail_value_cost',
};

/**
 * REV-21964 · U2 - the upstream label, as a TOTAL map over the closed union.
 *
 * `present` is deliberately absent: there is nothing to say about a file that is where it belongs,
 * and the notice is not rendered at all in that case. Using `Exclude` rather than a `''` entry is
 * what makes the compiler insist on a key for any state that is ever added.
 */
const UPSTREAM_STATE_LABEL_KEYS: Record<Exclude<DocumentUpstreamState, 'present'>, string> = {
    deleted: 'abcp_upstream_state_deleted',
    movedOut: 'abcp_upstream_state_moved_out',
    replaced: 'abcp_upstream_state_replaced',
};

//REV-21368 — single document of the closing (Pure View). Contract: e2e-lab/document-detail.lab.spec.ts.
//
//SKELETON: the I/O below is final, the template is deliberately empty — the contract is red first.
//
//Dumb by construction (ADR-0009 §10): no store, no HTTP, no fetching of the file, no persistence.
//Every control EMITS; nothing self-applies. Assignment persistence is REV-21602, so these outputs
//stay unwired until that endpoint lands — the component must not need changing when it does.
//
//Authorities, in this order:
//  1. ADR-0009 (accepted 11.08.2026) — beats the prototype wherever both speak.
//  2. The LIVE prototype (Dokumente → any document name) — layout, order, German labels.
//  3. The PNG attached to the ticket — ONE state (the proposal), not the whole contract.
//
//Where (1) overrules the prototype:
//  · no `Löschen` in the header (§4) — the prototype still has it, in red, with a lock tooltip
//  · `Projekt-Akte` is enabled and selectable (§9a) — an earlier AC draft said disabled

/** Search matches number OR name OR account group — the group is not a column but is findable. */
const matchesTerm = (account: AccountOption, term: string): boolean =>
    account.number.toLowerCase().includes(term) ||
    account.name.toLowerCase().includes(term) ||
    (account.group ?? '').toLowerCase().includes(term);

//REV-21363 — die Vorschau (samt der Schema-Allowlist, die `bypassSecurityTrustResourceUrl`
//absichert — SRR REV-21368 F1) ist nach `components/document-preview/` GEWANDERT: der
//Konto-Dokumentbereich braucht dieselbe Vorschau, und eine Sicherheitskontrolle zu kopieren wäre
//das schlechteste verfügbare Ergebnis. Diese Seite rendert jetzt `<abcp-document-preview>`.
//Der `unsafe-preview`-Test in `e2e-lab/document-detail.lab.spec.ts` bleibt bewusst hier stehen:
//er beweist, dass die Kontrolle AN DIESER Aufrufstelle durch die Extraktion hindurch hält.

@Component({
    selector: 'abcp-document-detail',
    templateUrl: './document-detail.component.html',
    styleUrls: ['./document-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpDocumentDetailComponent implements AfterContentInit {
    /**
     * REV-21369 — the projected PDF viewer (ADR-0021 E6). Present ⇒ it replaces the preview. This
     * view never renders it and never fetches bytes; it only makes room.
     */
    @ContentChildren(AbcpDocumentViewerSlotDirective) private viewerSlots?: QueryList<AbcpDocumentViewerSlotDirective>;

    /** REV-21369 — the projected comment column, at the end of the sidebar. */
    @ContentChildren(AbcpDocumentNotesSlotDirective) private notesSlots?: QueryList<AbcpDocumentNotesSlotDirective>;

    /**
     * ⚠️ QueryLists plus `markForCheck`, not plain `@ContentChild`s: the host shows the viewer only once
     * the byte door answered and withdraws it on a 404. This view is OnPush, and a projection that
     * comes or goes does not mark it dirty — it would keep an empty viewer frame and hide the preview.
     */
    get viewerSlot(): boolean {
        return (this.viewerSlots?.length ?? 0) > 0;
    }

    get notesSlot(): boolean {
        return (this.notesSlots?.length ?? 0) > 0;
    }

    constructor(private readonly cdr: ChangeDetectorRef) {}

    ngAfterContentInit(): void {
        this.viewerSlots?.changes.subscribe(() => this.cdr.markForCheck());
        this.notesSlots?.changes.subscribe(() => this.cdr.markForCheck());
    }

    // ─── REV-21369 — the adjustable split between document and sidebar (prototype) ──────────────
    //
    // View-local state, like a scroll position: it changes nothing about the document, so it is not
    // an @Output and not in the store. The same minimum column width as the account workspace's panes
    // (320 px), so neither side can be dragged into uselessness.

    /** Lower bound in percent; the effective bound also honours {@link SPLIT_MIN_PX}. */
    readonly SPLIT_MIN = 20;
    private static readonly SPLIT_MIN_PX = 320;
    private static readonly SPLIT_KEY_STEP = 5;

    /** Width of the document column, in percent of the body. 50 = the prototype's even split. */
    splitPercent = 50;
    splitDrag: { startX: number; startPercent: number; width: number } | null = null;

    @ViewChild('body') private body?: ElementRef<HTMLElement>;

    /** `fr`, not `%`: the 5 px divider sits between them and must not push the sidebar off-screen. */
    get gridColumns(): string {
        return `minmax(0, ${this.splitPercent}fr) 5px minmax(0, ${100 - this.splitPercent}fr)`;
    }

    onSplitStart(event: MouseEvent): void {
        const width = this.body?.nativeElement.getBoundingClientRect().width ?? 0;
        if (event.button !== 0 || width === 0) {
            return;
        }
        // Without this the drag selects text in the sidebar and in the PDF's text layer.
        event.preventDefault();
        this.splitDrag = { startX: event.clientX, startPercent: this.splitPercent, width };
    }

    @HostListener('document:mousemove', ['$event'])
    onSplitMove(event: MouseEvent): void {
        if (this.splitDrag === null) {
            return;
        }
        const { startX, startPercent, width } = this.splitDrag;
        this.splitPercent = this.clampSplit(startPercent + ((event.clientX - startX) / width) * 100, width);
    }

    @HostListener('document:mouseup')
    onSplitEnd(): void {
        this.splitDrag = null;
    }

    /** Keyboard: ←/→ move by 5 %, Home/End to the bounds, Enter resets. A separator must be operable without a mouse. */
    onSplitKey(event: KeyboardEvent): void {
        const width = this.body?.nativeElement.getBoundingClientRect().width ?? 0;
        const step = AbcpDocumentDetailComponent.SPLIT_KEY_STEP;
        const next: Record<string, number> = {
            ArrowLeft: this.splitPercent - step,
            ArrowRight: this.splitPercent + step,
            Home: 0,
            End: 100,
            Enter: 50,
        };
        if (!(event.key in next) || width === 0) {
            return;
        }
        event.preventDefault();
        this.splitPercent = this.clampSplit(next[event.key], width);
    }

    resetSplit(): void {
        this.splitPercent = 50;
    }

    private clampSplit(percent: number, width: number): number {
        const min = Math.max(this.SPLIT_MIN, (AbcpDocumentDetailComponent.SPLIT_MIN_PX / width) * 100);
        // A body narrower than two minimum columns cannot honour both — then it stays even.
        if (min >= 50) {
            return 50;
        }
        return Math.round(Math.min(100 - min, Math.max(min, percent)) * 10) / 10;
    }

    /** `null` while loading or on error — the page has no document to show. */
    @Input() document: DocumentDetail | null = null;

    /** The whole account plan. Read-only here: choosing one only EMITS (REV-21602 persists). */
    @Input() accounts: AccountOption[] = [];

    @Input() loading = false;
    @Input() error = false;

    /**
     * The filing gesture: target only. Validity is its own control here (the standing panel), so
     * asking for it inside the picker would put the same question twice on one screen.
     */
    @Output() assignmentChange = new EventEmitter<DocumentAssignmentChangeEvent>();

    @Output() proposalAccept = new EventEmitter<string>();
    @Output() proposalReject = new EventEmitter<string>();

    // ─── #84 — I/O-SKELETT, Template absichtlich noch leer (Contract zuerst rot) ─────────────────
    //
    // N1 (die Meldung) und N2 (die Ruecknahme) waren zwei der drei Kompensationen der signierten
    // Risikoannahme E8 (#57/A30): mit der Auto-Zuweisung ab Confidence ≥ 90 ist K1 („ein Mensch
    // bestaetigt jede Zuordnung") entfallen. N1 ist ENTFERNT (Entscheid Janosch 08.10.2026, REV-22258;
    // Befund in `docs/security/REV-22236.md`, QRM-Sign-off offen). N2 bleibt: „Zuordnung entfernen".
    //
    // Contract: e2e-lab/document-detail-agent.lab.spec.ts (Sichtbares) und
    // e2e/tests/abcp/documents-proposal-wiring.spec.ts (Verdrahtung).

    /**
     * N2 — die Zuordnung ZURUECKNEHMEN, statt sie zu ersetzen.
     *
     * Eigene Ausgabe und nicht `assignmentChange`: `Aendern` fuehrt zwingend zu einer NEUEN
     * Zuordnung (der Waehler laesst sich nur mit einer Wahl verlassen), und „das war falsch, ich
     * weiss noch nicht was richtig ist" ist genau der Fall, den eine maschinelle Zuweisung
     * erzeugt. Der Container beantwortet das mit `DELETE :documentId/assignment`.
     */
    @Output() assignmentRemove = new EventEmitter<string>();

    /** Ein Bestaetigen/Ablehnen/Zuruecknehmen laeuft — der Knopf zeigt es und nimmt nichts mehr an. */
    @Input() proposalPending = false;

    /**
     * Das letzte Bestaetigen/Ablehnen/Zuruecknehmen ist FEHLGESCHLAGEN.
     *
     * Eigene Eingabe und nicht `error`: `error` heisst „das Dokument konnte nicht geladen werden"
     * und ersetzt die ganze Seite. Ein gescheiterter Schreibvorgang darf die Seite nicht wegnehmen.
     */
    @Input() proposalFailed = false;

    /**
     * A write the user started here is running: its part is greyed out and takes no
     * clicks, and the clicked button turns its icon into a spinner. The host clears it when done.
     */
    @Input() busy: DocumentDetailBusy | null = null;

    /**
     * The last write on this document failed (relevance, filing, validity or a proposal
     * write). The page says so and shows the state before the click again.
     */
    @Input() writeFailed = false;

    /** The standing `Gültigkeit` toggle — live in all three sidebar states. */
    @Output() validityChange = new EventEmitter<DocumentValidityChangeEvent>();

    /** ONE output for both directions, carrying the new value (ADR-0009 §4 — there is no delete). */
    @Output() markNotRelevant = new EventEmitter<MarkNotRelevantEvent>();

    /** Shows the `Zurück` button. `false` when the page header carries the back button. */
    @Input() showBack = true;

    /** `Zurück` — the container decides where back is. */
    @Output() close = new EventEmitter<void>();

    /** The picker filters its OWN option list; that is widget-internal, like any dropdown. */
    pickerSearch = '';

    /**
     * DERIVED, never stored (ADR-0009 §9). A filed document shows its assignment; another account is
     * chosen after `Zuordnung entfernen`, in the picker that then appears.
     */
    get sidebarState(): DocumentSidebarState {
        if (!this.document) return 'picker';
        if (this.isFiled) return 'assigned';
        return this.document.assignment === 'proposed' && !!this.document.account ? 'proposal' : 'picker';
    }

    /** REV-21869 #164 — never for a live proposal (W2): a proposal is not yet a filed document. */
    get interpretation(): DocumentInterpretationView | undefined {
        return this.document?.interpretation;
    }

    get showInterpretation(): boolean {
        return this.interpretation != null && this.sidebarState !== 'proposal';
    }

    get interpretationDataState(): string {
        return this.interpretation ? INTERPRETATION_DATA_STATE[this.interpretation.state] : '';
    }

    get interpretationStateLabelKey(): string {
        const state = this.interpretation?.state;
        // REV-22010 — a spent budget is not "switched off": its own headline, or the reason line under
        // the `disabled` label would contradict it.
        if (state === 'disabled' && this.interpretation?.reason === 'limitReached') {
            return INTERPRETATION_LIMIT_REACHED_LABEL_KEY;
        }
        return state != null && state !== 'value' ? INTERPRETATION_STATE_LABEL_KEY[state] : '';
    }

    /**
     * REV-22010 — WHY there is no value, as an i18n key from the TOTAL map, or `''` (nothing rendered)
     * when the backend sent no reason or one outside the closed list. Only ever under `none`/`disabled`:
     * `interpreting` and `noValue` are complete statements, and a reason next to them would be noise.
     */
    get interpretationReasonKey(): string {
        const interpretation = this.interpretation;
        if (interpretation == null || (interpretation.state !== 'none' && interpretation.state !== 'disabled')) {
            return '';
        }
        return feedbackKeyOf(INTERPRETATION_REASON_KEYS, interpretation.reason);
    }

    // --- REV-22010 · the AI-reading block and the "no proposal" notice ---------------------------

    /** The fixed keys of the reading block, for the template. */
    readonly readingKeys = READING_BLOCK_KEYS;
    readonly proposalRefusalTitleKey = PROPOSAL_REFUSAL_TITLE_KEY;

    /**
     * Whether the block renders at all: only when there is something to say — the read is not done
     * yet, it failed or waits (daily cap, unsupported format), or a finished read lost the structure.
     * A clean read renders nothing: "read, all fine" is what the user assumes anyway.
     */
    get hasReadingBlock(): boolean {
        return this.readingStateLabelKey !== '' || this.readingQualityKeys.length > 0;
    }

    /** `data-state` of the block — the wire state, or `read` when the backend reported a finished read. */
    get readingDataState(): string {
        return this.document?.readingState ?? (this.document?.readingQuality != null ? 'read' : '');
    }

    get readingStateLabelKey(): string {
        return feedbackKeyOf(READING_STATE_LABEL_KEYS, this.document?.readingState);
    }

    /** The quality lines of a finished read — none for a clean read. */
    get readingQualityKeys(): readonly { key: string; params: Record<string, number> }[] {
        return this.document?.readingQuality?.structure === 'FAIL'
            ? [{ key: READING_BLOCK_KEYS.structureFail, params: {} }]
            : [];
    }

    /**
     * REV-22010 — why the AI proposed no account, only where the question is open: the picker of an
     * UNFILED document, and never beside a live proposal (the backend already withholds it then; checked here
     * too, because the view must not depend on that).
     */
    get proposalRefusalKey(): string {
        if (this.isFiled || this.sidebarState !== 'picker') {
            return '';
        }
        return feedbackKeyOf(PROPOSAL_REFUSAL_KEYS, this.document?.proposalRefusal);
    }

    /** `Saldo`, `Marktwert`, `Einstandswert` — what a read value is. */
    valueLabelKey(label: DocumentValueLabel): string {
        return VALUE_LABEL_KEYS[label] ?? '';
    }

    /** `2023-12-31` → `31.12.2023`. */
    dateLabel(iso: string | undefined): string {
        return isoDateLabel(iso);
    }

    /** Filed = has an account OR sits in the Projekt-Akte. Both count as `Zugeordnet` (§9a). */
    // --- REV-21964 · U2 - der Upstream-Hinweis ---------------------------------------------------

    /**
     * Ob der Hinweis ueberhaupt erscheint. `present` und "kein Wert" sind derselbe Fall: SharePoint
     * hat zu einer Datei, die es nie gehalten hat, keine Meinung - und ein Hochladen ist genau das.
     */
    get hasUpstreamNotice(): boolean {
        const state = this.document?.upstreamState;
        return state != null && state !== 'present';
    }

    get upstreamStateLabelKey(): string {
        const state = this.document?.upstreamState;
        return state == null || state === 'present' ? '' : UPSTREAM_STATE_LABEL_KEYS[state];
    }

    /**
     * C2 - laut, wenn das Dokument ein Konto belegt, leise sonst.
     *
     * Die Entscheidung trifft der Container (`upstreamNoticeIsUrgent`); hier wird sie nur gelesen.
     * Sie aus `assignment` neu abzuleiten waere eine zweite Meinung ueber dieselbe Tatsache.
     */
    get upstreamNoticeKey(): string {
        const urgent = this.document?.upstreamNoticeIsUrgent === true;
        // ⚠️ `replaced` needs its OWN wording, and getting this wrong was a real defect: the shared
        // sentence says the file "is no longer in place in SharePoint", which is FALSE for a
        // replacement — the file is still there, somebody edited it. One sentence covering three
        // states read fine in review and said something untrue to the first person who edited a file.
        //
        // The replacement wording also carries the C5 decision out loud: the account does NOT move
        // with the new version. Leaving that implicit would strand the trustee at exactly the moment
        // they have to act.
        if (this.document?.upstreamState === 'replaced') {
            return urgent ? 'abcp_upstream_notice_replaced_assigned' : 'abcp_upstream_notice_replaced_unassigned';
        }
        return urgent ? 'abcp_upstream_notice_assigned' : 'abcp_upstream_notice_unassigned';
    }

    get isUpstreamNoticeUrgent(): boolean {
        return this.document?.upstreamNoticeIsUrgent === true;
    }

    get isFiled(): boolean {
        const filing = this.document?.filing;
        return filing === 'account' || filing === 'projectFile';
    }

    get isProjectFile(): boolean {
        return this.document?.filing === 'projectFile';
    }

    get filteredAccounts(): AccountOption[] {
        const term = this.pickerSearch.trim().toLowerCase();
        return term ? this.accounts.filter((account) => matchesTerm(account, term)) : this.accounts;
    }

    /** The `n von m` counter of the prototype. */
    get matchCount(): number {
        return this.filteredAccounts.length;
    }

    get totalCount(): number {
        return this.accounts.length;
    }

    /** `—` is no value, not a weak one — the normal state until REV-21358 lands (§8). */
    get confidenceLabel(): string {
        const confidence = this.document?.confidence;
        return confidence === null || confidence === undefined ? '—' : `${confidence}%`;
    }

    trackById(_index: number, item: { id: string }): string {
        return item.id;
    }

    // --- The running write --------------------------------------------------------------

    /**
     * Whether `part` is busy. Without a host `busy`, a proposal write still blocks the cards it can
     * change: the proposal and the assignment (accept, reject and taking an assignment back).
     */
    isBusy(part: DocumentDetailBusy['part']): boolean {
        if (this.busy !== null) {
            return this.busy.part === part;
        }
        return this.proposalPending && (part === 'proposal' || part === 'assignment');
    }

    /** Any write is running — no second one starts until it is back. */
    get anyBusy(): boolean {
        return this.busy !== null || this.proposalPending;
    }

    /**
     * The clicked button of a busy part: its icon becomes the spinner. `choice` narrows it to the one
     * button that was clicked. Not `disabled`: that reads as "not allowed", while this says "working".
     */
    spins(part: DocumentDetailBusy['part'], choice?: string): boolean {
        if (!this.isBusy(part)) {
            return false;
        }
        return choice === undefined || this.busy === null || this.busy.choice === choice;
    }

    /** The validity the panel shows: the picked one while its write runs, else the saved one. */
    get shownValidity(): DocumentValidity | undefined {
        return this.busy?.part === 'validity' ? (this.busy.choice as DocumentValidity) : this.document?.validity;
    }

    /** The picker target the user just picked, marked while the write runs. */
    isChosen(target: string): boolean {
        return this.busy?.part === 'assignment' && this.busy.choice === target;
    }

    /**
     * Both directions act at once, without a confirmation: nothing is deleted, and the same
     * button takes the mark back, so a dialog would only add a click.
     */
    onNotRelevantToggle(): void {
        if (this.anyBusy) return;
        if (!this.document) return;
        this.markNotRelevant.emit({ documentId: this.document.id, notRelevant: !this.document.notRelevant });
    }

    onClose(): void {
        this.close.emit();
    }

    quoteText(quote: string | undefined): string {
        return quoteTextOf(quote);
    }

    onProposalAccept(): void {
        if (this.anyBusy) return;
        if (this.document) this.proposalAccept.emit(this.document.id);
    }

    /**
     * Rejecting drops the proposal but assigns nothing (§9). It deliberately does NOT open the
     * picker itself: once the container answers, `assignment` is `open` and the derived state IS
     * the picker. Self-opening would move the view on an event the container might never apply.
     */
    onProposalReject(): void {
        if (this.anyBusy) return;
        if (this.document) this.proposalReject.emit(this.document.id);
    }

    /** N2 — its own gesture, not a picker step: see `assignmentRemove`'s doc comment. */
    onAssignmentRemove(): void {
        if (this.anyBusy) return;
        if (this.document) this.assignmentRemove.emit(this.document.id);
    }

    onPickerSearch(term: string): void {
        this.pickerSearch = term;
    }

    /** One step: choosing the account IS the whole gesture. */
    onAccountPick(account: AccountOption): void {
        if (this.anyBusy) return;
        if (!this.document) return;
        this.assignmentChange.emit({
            documentId: this.document.id,
            target: { kind: 'account', accountId: account.id },
        });
        this.closePicker();
    }

    /** Projekt-Akte is a full filing target, not "no account" (§9a). */
    onProjectFilePick(): void {
        if (this.anyBusy) return;
        if (!this.document) return;
        this.assignmentChange.emit({ documentId: this.document.id, target: { kind: 'projectFile' } });
        this.closePicker();
    }

    /** The standing `Gültigkeit` panel — the ONE place this surface asks how long a document holds. */
    onValidityPick(validity: DocumentValidity): void {
        if (this.anyBusy) return;
        if (!this.document || this.document.validity === validity) return;
        this.validityChange.emit({ documentId: this.document.id, validity });
    }

    private closePicker(): void {
        this.resetPicker();
    }

    private resetPicker(): void {
        this.pickerSearch = '';
    }
}
